import { IGoodsResourceShortProps } from '../../props/goods/goods';
import GeneralService from '../general';

export interface ISavedPackingListItem {
    id?: string;
    goodsId?: string;
    goods?: IGoodsResourceShortProps;
    supplier?: string;
    deliveryDate?: string;
    quantityAmount: string;
    quantityUnit: string;
    length: string;
    width: string;
    height: string;
    weightAmount: string;
    weightUnit: string;
    useTotalVolume: boolean;
    orderNumber: number;
}

export interface ISavedPackingListHistoryData {
    id: string;
    savedAt: string;
    source: 'saved' | 'draft' | 'tally_scan';
    orderId?: string;
    orderNumber?: string;
    containerId?: string;
    containerNumber?: string;
    companyName?: string;
    customerName?: string;
    itemCount: number;
    totalQuantity: number;
    totalVolumeM3: number;
    totalWeightKg: number;
    goods: ISavedPackingListItem[];
}

const STORAGE_KEY = 'jg_last_packing_list_history';
const HISTORY_LIST_KEY = 'jg_packing_list_history_list';
const MAX_HISTORY_ITEMS = 15;

const PackingListStorageService = {
    /**
     * Get list of all saved packing list history snapshots (newest first)
     */
    getList: (): ISavedPackingListHistoryData[] => {
        try {
            const raw = localStorage.getItem(HISTORY_LIST_KEY);
            if (!raw) {
                const oldSingle = PackingListStorageService.getLatest();
                if (oldSingle && oldSingle.goods && oldSingle.goods.length > 0) {
                    const migrated = [oldSingle];
                    localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(migrated));
                    return migrated;
                }
                return [];
            }
            const parsed = JSON.parse(raw) as ISavedPackingListHistoryData[];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Failed to read packing list history from localStorage', e);
            return [];
        }
    },

    /**
     * Get the single latest packing list snapshot
     */
    getLatest: (): ISavedPackingListHistoryData | null => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as ISavedPackingListHistoryData;
            return parsed && parsed.goods && parsed.goods.length > 0 ? parsed : null;
        } catch (e) {
            console.error('Failed to read latest packing list from localStorage', e);
            return null;
        }
    },

    /**
     * Save a snapshot into history
     */
    save: (entry: {
        id?: string;
        savedAt?: string;
        source?: 'saved' | 'draft' | 'tally_scan';
        orderId?: string;
        orderNumber?: string;
        containerId?: string;
        containerNumber?: string;
        companyName?: string;
        customerName?: string;
        goods: ISavedPackingListItem[];
    }): ISavedPackingListHistoryData | null => {
        try {
            const validGoods = (entry.goods || []).filter(g => g.goods || (g.supplier && g.supplier.trim() !== ''));
            if (validGoods.length === 0) return null;

            // Calculate totals
            let totalQuantity = 0;
            let totalVolumeM3 = 0;
            let totalWeightKg = 0;

            validGoods.forEach(g => {
                const qty = Number(g.quantityAmount) || 0;
                totalQuantity += qty;

                const vol = GeneralService.calculateVolume(g.length, g.width, g.height);
                totalVolumeM3 += (vol * (qty > 0 ? qty : 1));

                const weight = Number(g.weightAmount) || 0;
                const weightInKg = GeneralService.getWeightInKg(weight * (qty > 0 ? qty : 1), g.weightUnit || 'kg');
                totalWeightKg += weightInKg;
            });

            const snapshot: ISavedPackingListHistoryData = {
                id: entry.id || GeneralService.guid(),
                savedAt: entry.savedAt || new Date().toISOString(),
                source: entry.source || 'saved',
                orderId: entry.orderId,
                orderNumber: entry.orderNumber,
                containerId: entry.containerId,
                containerNumber: entry.containerNumber,
                companyName: entry.companyName,
                customerName: entry.customerName,
                itemCount: validGoods.length,
                totalQuantity: Math.round(totalQuantity * 100) / 100,
                totalVolumeM3: Math.round(totalVolumeM3 * 1000) / 1000,
                totalWeightKg: Math.round(totalWeightKg * 100) / 100,
                goods: validGoods
            };

            const list = PackingListStorageService.getList();

            // Check if there is an existing entry for the exact same order & container from draft or recent save
            const existingIdx = list.findIndex(item => 
                item.orderId === snapshot.orderId && 
                item.containerId === snapshot.containerId &&
                (new Date(snapshot.savedAt).getTime() - new Date(item.savedAt).getTime()) < 1000 * 60 * 60 * 4 // within 4 hours
            );

            if (existingIdx > -1) {
                list.splice(existingIdx, 1);
            }

            list.unshift(snapshot);
            const trimmed = list.slice(0, MAX_HISTORY_ITEMS);

            localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(trimmed));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));

            return snapshot;
        } catch (e) {
            console.error('Failed to save packing list snapshot to localStorage', e);
            return null;
        }
    },

    /**
     * Remove a snapshot by id
     */
    remove: (id: string) => {
        try {
            const list = PackingListStorageService.getList().filter(item => item.id !== id);
            localStorage.setItem(HISTORY_LIST_KEY, JSON.stringify(list));
            if (list.length > 0) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(list[0]));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            console.error('Failed to remove packing list snapshot', e);
        }
    },

    /**
     * Clear all history snapshots
     */
    clear: () => {
        try {
            localStorage.removeItem(HISTORY_LIST_KEY);
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to clear packing list history', e);
        }
    }
};

export default PackingListStorageService;
